import { Badge, Flex, Text } from "@mantine/core";
import { format } from "date-fns/format";

import { ResultDetail } from "./result-detail";
import { ResultsSection } from "./results-section";
import {
  ClientFitNonNegotiableStatus,
  ClientFitProcessingStatus,
  ClientFitTierLabel,
  type ClientFitCriterionResult,
  type ClientFitMissingEvidence,
  type ClientFitNonNegotiableResult,
  type ClientFitSummary,
  type Screening,
} from "@/types/screeners";

interface ClientFitSectionProps {
  screening: Screening;
}

const PROCESSING_STATUS_LABELS: Record<ClientFitProcessingStatus, string> = {
  [ClientFitProcessingStatus.New]: "Queued",
  [ClientFitProcessingStatus.InProgress]: "Processing",
  [ClientFitProcessingStatus.Complete]: "Complete",
  [ClientFitProcessingStatus.Error]: "Error",
  [ClientFitProcessingStatus.Stale]: "Reprocessing",
  [ClientFitProcessingStatus.Skipped]: "Skipped",
};

const PROCESSING_STATUS_COLORS: Record<ClientFitProcessingStatus, string> = {
  [ClientFitProcessingStatus.New]: "gray",
  [ClientFitProcessingStatus.InProgress]: "blue",
  [ClientFitProcessingStatus.Complete]: "teal",
  [ClientFitProcessingStatus.Error]: "red",
  [ClientFitProcessingStatus.Stale]: "yellow",
  [ClientFitProcessingStatus.Skipped]: "gray",
};

const TIER_COLORS: Record<ClientFitTierLabel, string> = {
  [ClientFitTierLabel.StrongMatch]: "teal",
  [ClientFitTierLabel.PotentialMatch]: "yellow",
  [ClientFitTierLabel.Review]: "red",
};

const NON_NEGOTIABLE_LABELS: Record<ClientFitNonNegotiableStatus, string> = {
  [ClientFitNonNegotiableStatus.Met]: "Met",
  [ClientFitNonNegotiableStatus.NotMet]: "Not met",
  [ClientFitNonNegotiableStatus.Unknown]: "Unknown",
};

const NON_NEGOTIABLE_COLORS: Record<ClientFitNonNegotiableStatus, string> = {
  [ClientFitNonNegotiableStatus.Met]: "teal",
  [ClientFitNonNegotiableStatus.NotMet]: "red",
  [ClientFitNonNegotiableStatus.Unknown]: "yellow",
};

const formatConfidence = (confidence: number | null) => {
  if (confidence === null) {
    return "Not available";
  }

  return `${Math.round(confidence * 100)}%`;
};

const formatCompletedAt = (completedAt: string | null) => {
  if (!completedAt) {
    return "Not available";
  }

  return format(new Date(completedAt), "LLL d, y 'at' h:mmaaa O");
};

const getClientFitBadge = (clientFit: ClientFitSummary) => {
  if (
    clientFit.processingStatus === ClientFitProcessingStatus.Complete &&
    clientFit.tierLabel
  ) {
    return (
      <Badge color={TIER_COLORS[clientFit.tierLabel]} variant="light">
        {clientFit.tierLabel}
      </Badge>
    );
  }

  if (clientFit.processingStatus) {
    return (
      <Badge
        color={PROCESSING_STATUS_COLORS[clientFit.processingStatus]}
        variant="light"
      >
        {PROCESSING_STATUS_LABELS[clientFit.processingStatus]}
      </Badge>
    );
  }

  return null;
};

const ClientFitCriterionList = ({
  emptyLabel,
  items,
}: {
  emptyLabel: string;
  items: ClientFitCriterionResult[];
}) => {
  if (items.length === 0) {
    return (
      <Text c="gray.6" size="sm">
        {emptyLabel}
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="xs">
      {items.map((item) => (
        <Flex direction="column" key={item.criterionId}>
          <Text fw={600} size="sm">
            {item.label}
          </Text>
          <Text c="gray.7" size="sm">
            {item.rationale}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const ClientFitNonNegotiableList = ({
  items,
}: {
  items: ClientFitNonNegotiableResult[];
}) => {
  if (items.length === 0) {
    return (
      <Text c="gray.6" size="sm">
        No required qualification details were returned.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="xs">
      {items.map((item) => (
        <Flex direction="column" key={item.criterionId}>
          <Flex align="center" gap="xs">
            <Text fw={600} size="sm">
              {item.label}
            </Text>
            <Badge color={NON_NEGOTIABLE_COLORS[item.status]} variant="light">
              {NON_NEGOTIABLE_LABELS[item.status]}
            </Badge>
          </Flex>
          <Text c="gray.7" size="sm">
            {item.rationale}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const ClientFitMissingEvidenceList = ({
  items,
}: {
  items: ClientFitMissingEvidence[];
}) => {
  if (items.length === 0) {
    return (
      <Text c="gray.6" size="sm">
        No missing evidence items were returned.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="xs">
      {items.map((item) => (
        <Flex direction="column" key={item.criterionId}>
          <Text fw={600} size="sm">
            {item.label}
          </Text>
          <Text c="gray.7" size="sm">
            {item.reason}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

export const ClientFitSection = ({ screening }: ClientFitSectionProps) => {
  const { clientFit } = screening;

  if (!clientFit) {
    return null;
  }

  const result = clientFit.result;

  return (
    <ResultsSection
      title="Client Fit"
      titleBadge={getClientFitBadge(clientFit)}
    >
      <Flex direction="column" gap="sm">
        <Flex direction="column" gap={4}>
          {clientFit.processingStatus && (
            <ResultDetail
              label="Status"
              value={PROCESSING_STATUS_LABELS[clientFit.processingStatus]}
            />
          )}
          {clientFit.profile && (
            <ResultDetail
              label="Profile"
              value={`${clientFit.profile.profileName ?? "Untitled profile"} v${clientFit.profile.profileVersionNumber}`}
            />
          )}
          <ResultDetail
            label="Completed"
            value={formatCompletedAt(clientFit.completedAt)}
          />
          {clientFit.nonNegotiableStatus && (
            <ResultDetail
              label="Required qualifications"
              value={
                <Badge
                  color={NON_NEGOTIABLE_COLORS[clientFit.nonNegotiableStatus]}
                  variant="light"
                >
                  {NON_NEGOTIABLE_LABELS[clientFit.nonNegotiableStatus]}
                </Badge>
              }
            />
          )}
          {result && (
            <ResultDetail
              label="Confidence"
              value={formatConfidence(result.confidence)}
            />
          )}
        </Flex>

        {clientFit.errorDescription && (
          <Text c="red.7" size="sm">
            {clientFit.errorDescription}
          </Text>
        )}

        {result ? (
          <>
            <Flex direction="column" gap={4}>
              <Text fw={600} size="sm">
                Recommendation rationale
              </Text>
              <Text c="gray.8" size="sm">
                {result.rationale}
              </Text>
            </Flex>

            <Flex direction="column" gap={4}>
              <Text fw={600} size="sm">
                Strengths
              </Text>
              <ClientFitCriterionList
                emptyLabel="No strength signals were returned."
                items={result.greenFlags}
              />
            </Flex>

            <Flex direction="column" gap={4}>
              <Text fw={600} size="sm">
                Review items
              </Text>
              <ClientFitCriterionList
                emptyLabel="No review items were returned."
                items={result.yellowFlags}
              />
            </Flex>

            <Flex direction="column" gap={4}>
              <Text fw={600} size="sm">
                Required qualifications
              </Text>
              <ClientFitNonNegotiableList items={result.nonNegotiables} />
            </Flex>

            <Flex direction="column" gap={4}>
              <Text fw={600} size="sm">
                Missing evidence
              </Text>
              <ClientFitMissingEvidenceList items={result.missingEvidence} />
            </Flex>
          </>
        ) : (
          <Text c="gray.7" size="sm">
            Client Fit details are not available for this session yet.
          </Text>
        )}
      </Flex>
    </ResultsSection>
  );
};
